import * as THREE from 'three';
import type { Model } from '../models/types.ts';
import type { Clip } from './clip.ts';
import { CREATURE_CLIPS } from './creatures/index.ts';
import { HUMAN_BONES, humanClips, type HeroClipSet } from './human.ts';

/** A rig ready to bake: pivots in index order and their names. */
export interface Skeleton {
  bones: THREE.Object3D[];
  names: string[];
}

/** What a rigged recipe needs: the skeleton, the clips and the key their samples are shared under. */
export interface RigSpec {
  key: string;
  skel: Skeleton;
  clips: Clip[];
}

/** The model's inner group (bone 0: whole-figure motion that isn't the instance's own). */
const inner = (m: Model): THREE.Object3D => m.root.getObjectByName('model') ?? m.root;

/** The humanoid skeleton of a `figure()` model. */
export const humanSkeleton = (m: Model): Skeleton | null => {
  const r = m.rig;
  if (!r) return null;
  const byName: Record<string, THREE.Object3D | undefined> = {
    root: inner(m), figure: r.figure, body: r.body, head: r.head, armL: r.armL, foreL: r.foreL, offhand: r.offhand,
    armR: r.armR, foreR: r.foreR, weapon: r.weapon, legL: r.legL, shinL: r.shinL, legR: r.legR, shinR: r.shinR, cape: r.cape,
  };
  const bones: THREE.Object3D[] = [];
  const names: string[] = [];
  for (const n of HUMAN_BONES) {
    const b = byName[n];
    if (b) {
      bones.push(b);
      names.push(n);
    }
  }
  return { bones, names };
};

/** A humanoid rig with a class's clips (shared by every tier and spectre of the class). */
export const humanRig = (m: Model, set: HeroClipSet): RigSpec | null => {
  const skel = humanSkeleton(m);
  return skel ? { key: `human:${set}:${skel.names.join(',')}`, skel, clips: humanClips(set) as Clip[] } : null;
};

/** A model rigged with `skel` and the clips registered for `kind` (samples shared under `key`). */
const creatureRig = (m: Model, kind: string, key: string): RigSpec | null => {
  const clips = CREATURE_CLIPS[kind];
  if (!m.skel || !clips) return null;
  return { key: `creature:${key}:${m.skel.names.join(',')}`, skel: m.skel, clips: clips() };
};

/** The rig of a library model key (`hero:cls:…`, `enemy:kind:…`, `bot:kind`, `golem:n`), or null when it has none yet. */
export const rigFor = (key: string, m: Model): RigSpec | null => {
  const [kind, a] = key.split(':');
  if (kind === 'hero' && a === 'bear') return creatureRig(m, 'bear', key);
  if (kind === 'hero' && a) return humanRig(m, a as HeroClipSet);
  // The Prestacaras in Cal's face is a man: the heroes' bones and a warrior's moves.
  if (kind === 'enemy' && a === 'calFace') return humanRig(m, 'warrior');
  if (kind === 'enemy' && a) return creatureRig(m, a, key);
  if (kind === 'bot' && a) return creatureRig(m, `bot:${a}`, key);
  if (kind === 'golem') return creatureRig(m, 'golem', key);
  return null;
};

/**
 * Hangs things added to a rigged model's inner group after it was built (a season's dress: hats, lanterns,
 * candles) on the bone they sit closest to, so they move with it: each goes to the bone of the nearest of the
 * model's own meshes.
 */
export const rehome = (m: Model, objs: THREE.Object3D[]): void => {
  if (!m.skel || objs.length === 0) return;
  const g = inner(m);
  m.root.updateMatrixWorld(true);
  const bones = new Set(m.skel.bones);
  const skip = new Set(objs);
  const box = new THREE.Box3();
  const centers: { c: THREE.Vector3; bone: THREE.Object3D }[] = [];
  const visit = (o: THREE.Object3D, bone: THREE.Object3D): void => {
    if (skip.has(o)) return;
    if (bones.has(o) && o !== bone) {
      // A child bone: what hangs under it belongs to it.
      for (const ch of o.children) visit(ch, o);
      return;
    }
    if ((o as THREE.Mesh).isMesh) {
      box.setFromObject(o);
      if (!box.isEmpty()) centers.push({ c: box.getCenter(new THREE.Vector3()), bone });
    }
    for (const ch of o.children) visit(ch, bone);
  };
  for (const ch of g.children) visit(ch, g);
  if (centers.length === 0) return;
  const c = new THREE.Vector3();
  for (const o of objs) {
    box.setFromObject(o);
    if (box.isEmpty()) o.getWorldPosition(c);
    else box.getCenter(c);
    let best = centers[0] as (typeof centers)[number];
    let bd = Infinity;
    for (const k of centers) {
      const d = k.c.distanceToSquared(c);
      if (d < bd) {
        bd = d;
        best = k;
      }
    }
    if (best.bone === g) continue;
    // A joint's own frame is its first child (`abs`); parts keep their place.
    const target = best.bone.children.find((ch) => !bones.has(ch) && !(ch as THREE.Mesh).isMesh) ?? best.bone;
    target.attach(o);
  }
};
